import {useState, useEffect, useRef} from 'preact/hooks'
import {formatSliderPrice} from "../utils/priceFormatter.ts";

interface PriceSliderProps {
    minPrice: number
    maxPrice: number
    onPriceChange: (min: number, max: number) => void
    activeFilter?: string[]
}

export function PriceSlider({
                                minPrice,
                                maxPrice,
                                onPriceChange,
                                activeFilter
                            }: PriceSliderProps) {
    // Floor lower end and ceil upper end to nearest 100
    const roundedMinPrice = Math.floor(minPrice / 100) * 100
    const roundedMaxPrice = Math.ceil(maxPrice / 100) * 100

    // Parse active filter (e.g. ["500-2000"]) for initial state on mount
    const parseActiveFilter = (): {min: number, max: number} | null => {
        if (!activeFilter || activeFilter.length === 0) return null
        const parts = activeFilter[0].split('-')
        if (parts.length !== 2) return null
        const min = parseInt(parts[0])
        const max = parseInt(parts[1])
        if (isNaN(min) || isNaN(max)) return null
        return { min, max }
    }
    const initialFilter = parseActiveFilter()
    const initialMin = initialFilter ? initialFilter.min : roundedMinPrice
    const initialMax = initialFilter ? initialFilter.max : roundedMaxPrice

    const [isDragging, setIsDragging] = useState<'min' | 'max' | null>(null)
    const [currentMin, setCurrentMin] = useState(initialMin)
    const [currentMax, setCurrentMax] = useState(initialMax)
    const [tempMin, setTempMin] = useState(initialMin)
    const [tempMax, setTempMax] = useState(initialMax)
    const [isFullRange, setIsFullRange] = useState<boolean>(false)

    const sliderRef = useRef<HTMLDivElement>(null)
    const rafRef = useRef<number | null>(null)
    const [prevMinPrice, setPrevMinPrice] = useState(roundedMinPrice)
    const [prevMaxPrice, setPrevMaxPrice] = useState(roundedMaxPrice)

    // Update temp values when not dragging
    useEffect(() => {
        if (!isDragging) {
            setTempMin(currentMin)
            setTempMax(currentMax)
        }
    }, [currentMin, currentMax, isDragging])

    // Only reset values when they are outside the new range or when min/max are at their limits
    useEffect(() => {
        const newRoundedMinPrice = Math.floor(minPrice / 100) * 100
        const newRoundedMaxPrice = Math.ceil(maxPrice / 100) * 100

        let newMin = currentMin
        let newMax = currentMax

        if (newRoundedMinPrice !== prevMinPrice && currentMin === prevMinPrice) {
            newMin = newRoundedMinPrice
        } else if (currentMin < newRoundedMinPrice) {
            newMin = newRoundedMinPrice
        }

        if (newRoundedMaxPrice !== prevMaxPrice && currentMax === prevMaxPrice) {
            newMax = newRoundedMaxPrice
        } else if (currentMax > newRoundedMaxPrice) {
            newMax = newRoundedMaxPrice
        }

        if (newMin !== currentMin || newMax !== currentMax) {
            setCurrentMin(newMin)
            setCurrentMax(newMax)
            setTempMin(newMin)
            setTempMax(newMax)
        }

        setPrevMinPrice(newRoundedMinPrice)
        setPrevMaxPrice(newRoundedMaxPrice)
    }, [minPrice, maxPrice, currentMin, currentMax, prevMinPrice, prevMaxPrice])

    // Unified handler for calculating value from position with 100 steps
    const calculateValue = (clientX: number): number => {
        if (!sliderRef.current) return 0

        const rect = sliderRef.current.getBoundingClientRect()
        const percentage = Math.min(Math.max((clientX - rect.left) / rect.width, 0), 1)
        const rawValue = roundedMinPrice + percentage * (roundedMaxPrice - roundedMinPrice)

        // Round to nearest 100
        return Math.round(rawValue / 100) * 100
    }

    // Unified handler for updating position
    const updatePosition = (clientX: number) => {
        if (!isDragging) return

        // Cancel previous animation frame
        if (rafRef.current) {
            cancelAnimationFrame(rafRef.current)
        }

        // Use requestAnimationFrame for smooth updates
        rafRef.current = requestAnimationFrame(() => {
            const value = calculateValue(clientX)

            if (isDragging === 'min') {
                setTempMin(Math.min(value, tempMax - 100))
            } else {
                setTempMax(Math.max(value, tempMin + 100))
            }
        })
    }

    const handleStart = (handle: 'min' | 'max') => (e: MouseEvent | TouchEvent) => {
        setIsDragging(handle)
        e.preventDefault()
        e.stopPropagation()
    }

    const handleMouseMove = (e: MouseEvent) => {
        updatePosition(e.clientX)
    }

    const handleTouchMove = (e: TouchEvent) => {
        if (e.touches.length > 0) {
            updatePosition(e.touches[0].clientX)
        }
    }

    const handleEnd = () => {
        if (isDragging) {
            setCurrentMin(tempMin)
            setCurrentMax(tempMax)
            onPriceChange(tempMin, tempMax)
        }
        setIsDragging(null)

        // Cancel any pending animation frame
        if (rafRef.current) {
            cancelAnimationFrame(rafRef.current)
            rafRef.current = null
        }
    }

    // Global event listeners for dragging (Mouse + Touch)
    useEffect(() => {
        if (isDragging) {
            // Mouse events
            document.addEventListener('mousemove', handleMouseMove)
            document.addEventListener('mouseup', handleEnd)

            // Touch events
            document.addEventListener('touchmove', handleTouchMove, { passive: false })
            document.addEventListener('touchend', handleEnd)
            document.addEventListener('touchcancel', handleEnd)

            return () => {
                document.removeEventListener('mousemove', handleMouseMove)
                document.removeEventListener('mouseup', handleEnd)
                document.removeEventListener('touchmove', handleTouchMove)
                document.removeEventListener('touchend', handleEnd)
                document.removeEventListener('touchcancel', handleEnd)
            }
        }
    }, [isDragging, tempMin, tempMax, minPrice, maxPrice])

    // Use temp values while dragging, otherwise use current values
    const displayMin = isDragging ? tempMin : currentMin
    const displayMax = isDragging ? tempMax : currentMax

    const minPercent = ((displayMin - roundedMinPrice) / (roundedMaxPrice - roundedMinPrice)) * 100
    const maxPercent = ((displayMax - roundedMinPrice) / (roundedMaxPrice - roundedMinPrice)) * 100
    const width = maxPercent - minPercent;

    useEffect(() => {
        if(width > 99.5) {
            setIsFullRange(true)
        } else {
            setIsFullRange(false)
        }
    }, [width]);

    // Reset slider when price filter is cleared externally (e.g. "Alle löschen")
    useEffect(() => {
        if (!activeFilter || activeFilter.length === 0) {
            setCurrentMin(roundedMinPrice)
            setCurrentMax(roundedMaxPrice)
            setTempMin(roundedMinPrice)
            setTempMax(roundedMaxPrice)
        }
    }, [activeFilter]);

    const hasEqualMinMax = roundedMinPrice === roundedMaxPrice;

    return (
        <>
            {!hasEqualMinMax &&
                <div className="aside-section">
                    <h3>Preis</h3>
                    <div
                        ref={sliderRef}
                        className={`price-slider mx-auto w-[calc(100%_-_16px)] lg:w-full relative h-1 rounded-full cursor-pointer ${isFullRange ? 'bg-theme-black' : 'bg-theme-gray-light'}`}
                        style={{touchAction: 'none', userSelect: 'none'}}
                    >
                        {/* Active range */}
                        <div
                            className={`h-1 rounded-full absolute top-0 pointer-events-none ${isFullRange ? 'bg-theme-black' : 'bg-theme-primary'}`}
                            style={{
                                left: `${minPercent}%`,
                                width: `${width}%`,
                                willChange: isDragging ? 'left, width' : 'auto'
                            }}
                        />

                        {/* Min handle */}
                        <div
                            className={`w-4 h-4 rounded-full absolute -top-1.5 cursor-grab active:cursor-grabbing ${isFullRange ? 'bg-theme-black' : 'bg-theme-primary'}`}
                            style={{
                                left: `${minPercent}%`,
                                marginLeft: '-8px',
                                willChange: isDragging === 'min' ? 'left' : 'auto'
                            }}
                            onMouseDown={handleStart('min')}
                            onTouchStart={handleStart('min')}
                        />

                        {/* Max handle */}
                        <div
                            className={`w-4 h-4 rounded-full absolute -top-1.5 cursor-grab active:cursor-grabbing ${isFullRange ? 'bg-theme-black' : 'bg-theme-primary'}`}
                            style={{
                                left: `${maxPercent}%`,
                                marginLeft: '-8px',
                                willChange: isDragging === 'max' ? 'left' : 'auto'
                            }}
                            onMouseDown={handleStart('max')}
                            onTouchStart={handleStart('max')}
                        />
                    </div>

                    <div className="flex text-14px gap-0.5 mt-5 items-center justify-between">
                        {(displayMin !== roundedMinPrice || displayMax !== roundedMaxPrice) && (
                            <div className="flex text-14px gap-1 items-center">
                                <span>€ {formatSliderPrice(displayMin)}</span> <span>-</span> <span>€ {formatSliderPrice(displayMax)}</span>
                            </div>
                        )}
                        {displayMin === roundedMinPrice && displayMax === roundedMaxPrice && (
                            <div className="">beliebig</div>
                        )}
                    </div>

                </div>
            }
        </>
    )
}
